import { API_ADDRESS, STRIPE_PAYWALL_URL } from '@/constants/env';
import { v4 as uuid } from 'uuid';
import { UserData, InvitationData } from '../types';
import { UserPreferenceDto } from '@/domain/userSettings/userSettingsDtos';
import { PREFERENCE_NAMES, SCOPE_GLOBAL } from '../constants';

export const capitalizeFirstLetter = (str: string): string => {
  return str?.charAt(0)?.toUpperCase() + str?.slice(1)?.toLowerCase();
};

export const getLoginAddress = (): string => {
  return API_ADDRESS.includes('seddi')
    ? API_ADDRESS.replace('api', 'login')
    : 'http://localhost:3001';
};

export const generateLogoutUrl = (): string => {
  // Get the current URL
  const currentUrl = window.location.href;

  // Encode the URL to be safely used as a query parameter
  const encodedUrl = encodeURIComponent(currentUrl);

  const cacheBust = uuid();

  // Construct the login URL with the appUrl parameter
  const logOutUrl = `${API_ADDRESS}/decorator-permissions/v1/login-redirect?gcp-iap-mode=GCIP_SIGNOUT&appUrl=${encodedUrl}&c=${cacheBust}`;

  return logOutUrl;
};

export const formatDisplayName = (firstName?: string, lastName?: string): string => {
  return `${firstName || ''} ${lastName || ''}`.trim();
};

export const getInitials = (name?: string): string => {
  return name?.charAt(0)?.toUpperCase() || 'C';
};

export const lastActivityConverter = (d: string) => {
  const lastActivity = new Date(d);
  const daysAgo = Math.floor(
    (new Date().getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24),
  );

  const timeAgo = (days: number) => {
    if (days === 0) {
      return 'Today';
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 30) {
      return `${days} days ago`;
    } else if (days < 60) {
      return '1 month ago';
    } else if (days < 365) {
      return `${Math.floor(days / 30)} months ago`;
    } else {
      return 'More than a year ago';
    }
  };

  return timeAgo(daysAgo);
};

export const getUIRole = (role: string, permission: string): string => {
  const permissionText = permission ? capitalizeFirstLetter(permission) : 'Inactive';

  if (role === 'owner') {
    return `Owner - ${permissionText}`;
  }

  return permissionText;
};

export const navigateToPaywall = (orgId: string, email?: string) => {
  const encodedEmail = encodeURIComponent(email || '');
  const emailParam = email ? `&prefilled_email=${encodedEmail}` : '';

  const separator = STRIPE_PAYWALL_URL.includes('?') ? '&' : '?';
  window.open(
    `${STRIPE_PAYWALL_URL}${separator}client_reference_id=${orgId}${emailParam}`,
    '_blank',
  );
};
export const formatInvitationDate = (createdAt?: string, invitedAt?: string) => {
  const dateString = createdAt || invitedAt || 'Unknown';
  const date = new Date(dateString);

  const formattedDate = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return {
    date: formattedDate,
    time: formattedTime,
    full: `${formattedDate} ${formattedTime}`,
  };
};

export const getPreferenceValue = (
  preferences: UserPreferenceDto[] | undefined,
  preferenceName: string,
  defaultValue: string = '',
): string => {
  return (
    preferences?.find(
      (preference: UserPreferenceDto) =>
        preference.name.toLowerCase() === preferenceName.toLowerCase(),
    )?.value || defaultValue
  );
};

export const transformUserData = (user: any, permissions: any[], orgUsers: any[]): UserData => {
  const name = getPreferenceValue(user.preferences, PREFERENCE_NAMES.NAME);
  const lastName = getPreferenceValue(user.preferences, PREFERENCE_NAMES.LAST_NAME);
  const displayName = user.displayName;

  return {
    ...user,
    id: user.uid,
    userId: user.uid,
    name: name || '',
    lastName: lastName || '',
    displayName: displayName || user.email,
    title: getPreferenceValue(user.preferences, PREFERENCE_NAMES.TITLE),
    phoneNumber: getPreferenceValue(user.preferences, PREFERENCE_NAMES.PHONE_NUMBER),
    unitSystem: getPreferenceValue(user.preferences, PREFERENCE_NAMES.UNITS),
    permission: permissions.find((permission: any) => permission.userId === user.uid)?.permission,
    role: orgUsers.find((orgUser: any) => orgUser.userId === user.uid)?.role,
    lastActivity: user.lastActivity || lastActivityConverter(user.lastActivityTime),
  };
};

export const calculateUserStats = (users: UserData[]) => {
  return users.reduce(
    (acc, user) => {
      const permission = user.permission?.toLowerCase();
      if (permission === 'editor') {
        acc.editors++;
      } else if (permission === 'viewer') {
        acc.viewers++;
      } else {
        acc.inactive++;
      }
      return acc;
    },
    { editors: 0, viewers: 0, inactive: 0 },
  );
};

export const calculateInvitationStats = (invitations: InvitationData[]) => {
  return invitations.reduce(
    (acc, invitation) => {
      const permission = invitation.permission?.toLowerCase();
      if (permission === 'editor') {
        acc.editors++;
      } else if (permission === 'viewer') {
        acc.viewers++;
      } else {
        acc.others++;
      }
      return acc;
    },
    { editors: 0, viewers: 0, others: 0 },
  );
};

export const calculateTotalEditors = (users: UserData[], invitations: InvitationData[]) => {
  return (
    users.filter(user => user.permission === 'editor').length +
    invitations.filter(invitation => invitation.permission === 'editor').length
  );
};

export const getUserDisplayName = (user: UserData): string => {
  return user.displayName || `${user.name || ''} ${user.lastName || ''}`.trim() || user.email;
};

export const isLastOwner = (users: UserData[], userId: string): boolean => {
  const currentOwners = users.filter(user => user.role === 'owner').length;
  const selectedUser = users.find(user => user.id === userId);
  return currentOwners <= 1 && selectedUser?.role === 'owner';
};

export const wouldExceedSeatLimit = (
  users: UserData[],
  invitations: InvitationData[],
  seatLimit: number,
  newEditorCount: number = 1,
): boolean => {
  const currentEditors = users.filter(user => user.permission === 'editor').length;
  const pendingEditors = invitations.filter(
    invitation => invitation.permission === 'editor',
  ).length;
  const totalEditors = currentEditors + pendingEditors + newEditorCount;

  return totalEditors > seatLimit;
};

export const sortArray = <T>(
  array: T[],
  sortField: string | null,
  sortDirection: 'asc' | 'desc',
  getValue: (item: T, field: string) => string,
): T[] => {
  if (!sortField) return array;

  return [...array].sort((a, b) => {
    const aValue = getValue(a, sortField);
    const bValue = getValue(b, sortField);

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
};

const getUserFieldValue = (user: UserData, field: string): string => {
  switch (field) {
    case 'name':
      return user.displayName.toLowerCase();
    case 'role':
      return user.permission || '';
    case 'lastActivity':
      return user.lastActivity || 'Never';
    default:
      return '';
  }
};

const getInvitationFieldValue = (invitation: InvitationData, field: string): string => {
  switch (field) {
    case 'email':
      return invitation.email?.toLowerCase() || '';
    case 'role':
      return invitation.permission || '';
    case 'invitedAt':
      return invitation.createdAt || invitation.invitedAt || '';
    default:
      return '';
  }
};

export const sortUsers = (
  users: UserData[],
  sortField: string | null,
  sortDirection: 'asc' | 'desc',
): UserData[] => {
  return sortArray(users, sortField, sortDirection, getUserFieldValue);
};

/**
 * Sort invitations array
 */
export const sortInvitations = (
  invitations: InvitationData[],
  sortField: string | null,
  sortDirection: 'asc' | 'desc',
): InvitationData[] => {
  return sortArray(invitations, sortField, sortDirection, getInvitationFieldValue);
};

export const createSortHandler = <T extends string>(
  currentField: T | null,
  currentDirection: 'asc' | 'desc',
  setField: (field: T | null) => void,
  setDirection: (direction: 'asc' | 'desc') => void,
) => {
  return (field: T) => {
    if (currentField === field) {
      setDirection(currentDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setField(field);
      setDirection('asc');
    }
  };
};

export const FIELD_TO_PREFERENCE_MAP: Record<
  'name' | 'lastName' | 'title' | 'phoneNumber',
  string
> = {
  lastName: PREFERENCE_NAMES.LAST_NAME,
  phoneNumber: PREFERENCE_NAMES.PHONE_NUMBER,
  name: PREFERENCE_NAMES.NAME,
  title: PREFERENCE_NAMES.TITLE,
};

export const createUserPreference = (preferenceName: string, value: string) => ({
  scope: SCOPE_GLOBAL,
  name: preferenceName,
  value: value,
});

export const createDisplayNamePreference = (displayName: string) => ({
  scope: SCOPE_GLOBAL,
  name: 'displayName',
  value: displayName,
});

export const requiresDisplayNameUpdate = (
  field: 'name' | 'lastName' | 'title' | 'phoneNumber',
): boolean => {
  return field === 'name' || field === 'lastName';
};

export const createInputHandlers = (
  field: 'name' | 'lastName' | 'title' | 'phoneNumber',
  localInputs: { name: string; lastName: string; title: string },
  setLocalInputs: React.Dispatch<
    React.SetStateAction<{ name: string; lastName: string; title: string }>
  >,
  selectedUser: UserData | null,
  handleUserUpdate: (params: {
    field: 'name' | 'lastName' | 'title' | 'phoneNumber';
    value: string;
  }) => void,
) => ({
  onChange: ({ value }: { value: string }) => setLocalInputs(prev => ({ ...prev, [field]: value })),
  onBlur: () => {
    if (
      selectedUser &&
      selectedUser[field as keyof typeof selectedUser] !==
        localInputs[field as keyof typeof localInputs]
    ) {
      handleUserUpdate({ field, value: localInputs[field as keyof typeof localInputs] });
    }
  },
});
